import { Box, Drawer, IconButton, Stack, Typography, Button, Chip, Collapse } from "@compass/core";
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PieChartIcon from '@mui/icons-material/PieChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarMonthSharpIcon from '@mui/icons-material/CalendarMonthSharp';
import LocalAtmSharpIcon from '@mui/icons-material/LocalAtmSharp';
import LocalLibrarySharpIcon from '@mui/icons-material/LocalLibrarySharp';
import SchoolSharpIcon from '@mui/icons-material/SchoolSharp';
import SettingsSharpIcon from '@mui/icons-material/SettingsSharp';
import PhonelinkRingSharpIcon from '@mui/icons-material/PhonelinkRingSharp';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { DashboardCardConfig } from '../types';
import { CATEGORY_CONFIGS } from '../config/categoryConfigs';
import { CardCategory } from '../types/CardCategory';
import SchoolIcon from '@mui/icons-material/School';
import { useState, useMemo, useEffect } from 'react';


interface CardConfigurationDrawerProps {
  open: boolean;
  onClose: () => void;
  cards: DashboardCardConfig[];
  onToggleCard: (cardId: string) => void;
}

const CARD_ICON_MAP: Record<string, React.ElementType> = {
  'BarChart': BarChartIcon,
  'TableChart': TableChartIcon,
  'PieChart': PieChartIcon,
  'Checklist': ChecklistIcon,
  'School': SchoolIcon,
  'DonutLarge': DonutLargeIcon,
  'TableRows': TableRowsIcon, 
};

const CATEGORY_ICON_MAP: Record<string, React.ElementType> = {
    'CalendarMonthSharp': CalendarMonthSharpIcon,
    'LocalAtmSharp': LocalAtmSharpIcon,
    'LocalLibrarySharp': LocalLibrarySharpIcon,
    'SchoolSharp': SchoolSharpIcon,
    'PhonelinkRingSharp': PhonelinkRingSharpIcon,
    'SettingsSharp': SettingsSharpIcon,
};

export const CardConfigurationDrawer = ({ 
  open, 
  onClose, 
  cards,
  onToggleCard 
}: CardConfigurationDrawerProps) => {

    const [expandedCategories, setExpandedCategories] = useState<Set<CardCategory>>(new Set());

    // Reset on drawer closes
    useEffect(() => {
        if (!open) {
            setExpandedCategories(new Set());
        }
    }, [open]);


    const cardsByCategory = useMemo(() => {
        const grouped = new Map<CardCategory, DashboardCardConfig[]>();

        cards.forEach(card => {
            if (!grouped.has(card.category)) {
                grouped.set(card.category, []);
            }
            grouped.get(card.category)!.push(card);
        });

        return grouped;
    }, [cards]);

    const toggleCategory = (categoryId: CardCategory) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };
  
  const getCardIconComponent = (iconName: string) => {
    const IconComponent = CARD_ICON_MAP[iconName] || BarChartIcon;
    return <IconComponent />;
    };

  const getCategoryIconComponent = (iconName: string) => {
    const IconComponent = CATEGORY_ICON_MAP[iconName];
    return IconComponent ? <IconComponent /> : null;
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 400,
          maxWidth: '90vw',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h5">Add Cards</Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close drawer">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Card List by Category */}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <Stack direction="column" spacing="sm">
            {CATEGORY_CONFIGS.map((category) => {
              const categoryCards = cardsByCategory.get(category.id) || [];
              const isExpanded = expandedCategories.has(category.id);
              
              // Skip empty categories
              if (categoryCards.length === 0) return null;

              return (
                <Box key={category.id}>
                  {/* Category Header */}
                  <Box
                    onClick={() => toggleCategory(category.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      cursor: 'pointer',
                      borderRadius: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'primary.main',
                      }}
                    >
                      {getCategoryIconComponent(category.icon)}
                    </Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                      {category.label}
                    </Typography>
                    <Chip 
                      label={categoryCards.length} 
                      size="small" 
                      sx={{ minWidth: 28 }}
                    />
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>

                  {/* Category Cards */}
                  <Collapse in={isExpanded}>
                    <Stack direction="column" spacing="sm" sx={{ mt: 1, ml: 1 }}>
                      {categoryCards.map((card) => (
                        <Box
                          key={card.id}
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          {/* Icon */}
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: card.enabled ? 'primary.main' : 'action.selected',
                              color: card.enabled ? 'primary.contrastText' : 'text.secondary',
                              flexShrink: 0,
                            }}
                          >
                            {getCardIconComponent(card.icon)}
                          </Box>

                          {/* Content */}
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing="sm" alignItems="center" sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                {card.name}
                              </Typography>
                              {card.isNew && (
                                <Chip 
                                  label="New" 
                                  size="small" 
                                  color="secondary"
                                  sx={{ height: 20, fontSize: '0.75rem' }}
                                />
                              )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {card.description}
                            </Typography>
                          </Box>

                          {/* Action Button */}
                          <Box sx={{ flexShrink: 0 }}>
                            {card.enabled ? (
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckIcon />}
                                onClick={() => onToggleCard(card.id)}
                                sx={{ minWidth: 100 }}
                              >
                                Added
                              </Button>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onToggleCard(card.id)}
                                sx={{ minWidth: 100 }}
                              >
                                Add
                              </Button>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};